import { RUPEE_SYMBOL } from '../../constants'

export function calculateYield(interestRate: number, yearlyCompoundingFrequency: number): number {
  if (interestRate > 0 && yearlyCompoundingFrequency > 0 && yearlyCompoundingFrequency <= 365) {
    const rateDecimal = interestRate / 100.0
    const ear = Math.pow(1.0 + rateDecimal / yearlyCompoundingFrequency, yearlyCompoundingFrequency) - 1.0
    // Round to two decimal places
    return Math.round(ear * 100) / 100
  } else {
    return 0.0
  }
}

export const getFDFlowRedirectionCTA = ({
  institutionId,
  isNavigationDeferred,
  navigationStyle
}: {
  institutionId: string
  isNavigationDeferred: boolean
  navigationStyle: string
}) => {
  return {
    type: 'CTA',
    ctaType: 'DEFAULT',
    method: 'GET',
    navigationStyle: navigationStyle,
    isNavigationDeferred: isNavigationDeferred,
    url: `/v1/power-fd/upswing-redirection?institutionId=${institutionId}&from=WEB`
  }
}

export const formatIndianNumber = (num: string | number, isCurrency: boolean = false): string => {
  if (num === null || num === undefined) return ''

  // Convert to string and remove existing commas
  let numStr = String(num).replace(/,/g, '')

  // Check if it's a valid number
  if (!/^-?\d*\.?\d*$/.test(numStr)) return String(num)

  // Determine if the number is negative and remove the sign for formatting
  const isNegative = numStr.startsWith('-')
  if (isNegative) {
    numStr = numStr.substring(1) // Remove '-' for formatting
  }

  const [integerPart, decimalPart] = numStr.split('.')

  let lastThree = integerPart.substring(integerPart.length - 3)
  const otherNumbers = integerPart.substring(0, integerPart.length - 3)

  let formattedIntegerPart = ''
  if (otherNumbers !== '') {
    // Apply Indian comma formatting for numbers greater than 999
    formattedIntegerPart = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree
  } else {
    formattedIntegerPart = lastThree // If less than 1000, no additional commas
  }

  // Add back the negative sign if applicable
  let result = (isNegative ? '-' : '') + (isCurrency ? `${RUPEE_SYMBOL}` : '') + formattedIntegerPart

  return decimalPart !== undefined ? result + '.' + decimalPart : result
}

export const formatIndianNumberWithDecimals = (
  num: string | number,
  noOfDecimals: number,
  isCurrency: boolean = false
): string => {
  if (num === null || num === undefined) return ''

  // 1. Get the initial Indian formatted string using the (now fixed) formatIndianNumber.
  // This handles integer commas and the negative sign, but not decimal precision.
  const rawIndianFormatted = formatIndianNumber(num)

  // 2. Remove commas from the raw formatted string to prepare for numeric conversion.
  // This also handles the negative sign that formatIndianNumber correctly placed.
  let cleanNumStr = rawIndianFormatted.replace(/,/g, '')

  // 3. Extract the sign again and work with the absolute value for toFixed.
  const isNegative = cleanNumStr.startsWith('-')
  if (isNegative) {
    cleanNumStr = cleanNumStr.substring(1)
  }

  const numValue = parseFloat(cleanNumStr)

  // Fallback if parsing fails (shouldn't happen with valid numbers after formatIndianNumber)
  if (isNaN(numValue)) {
    return rawIndianFormatted
  }

  // 4. Apply toFixed for rounding and decimal padding based on noOfDecimals.
  const fixedNumStr = numValue.toFixed(noOfDecimals)

  // 5. Split the result of toFixed into integer and decimal parts.
  let [integerPartFixed, decimalPartFixed] = fixedNumStr.split('.')

  // 6. Re-apply Indian formatting to the integer part obtained after toFixed.
  // This is crucial because toFixed might have rounded up, potentially changing
  // the integer part (e.g., 99.99 to 100.00).
  let lastThree = integerPartFixed.substring(integerPartFixed.length - 3)
  const otherNumbers = integerPartFixed.substring(0, integerPartFixed.length - 3)

  let reFormattedIntegerPart = ''
  if (otherNumbers !== '') {
    reFormattedIntegerPart = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree
  } else {
    reFormattedIntegerPart = lastThree
  }

  // 7. Combine the sign, re-formatted integer part, and decimal part.
  let result = (isNegative ? '-' : '') + (isCurrency ? `${RUPEE_SYMBOL}` : '') + reFormattedIntegerPart

  // 8. Append decimal part only if noOfDecimals is greater than 0.
  if (noOfDecimals > 0) {
    result += '.' + decimalPartFixed
  }

  return result
}

export const calculateMaturityAmount = (principal: number, annualInterestRate: number, timeInYears: number): number => {
  if (principal < 0 || annualInterestRate < 0 || timeInYears < 0) {
    return 0
  }
  // Simple interest calculation: A = P * (1 + (R/100) * T)
  const maturityAmount = principal * (1 + (annualInterestRate / 100) * timeInYears)
  return maturityAmount
}

export const formatTimePeriod = (timeInYears: number): string => {
  const years = Math.floor(timeInYears)
  const months = Math.round((timeInYears - years) * 12)
  let parts: string[] = []
  if (years > 0) {
    parts.push(`${years}Y`)
  }
  if (months > 0) {
    parts.push(`${months}M`)
  }
  return parts.length > 0 ? parts.join(' ') : '0Y' // Ensure "0Y" for zero or negative time
}
